import settings from '@solinkcorp/settings';
import { ContextineAPI, OAuthApi } from 'clients';
import { type IEvent } from 'clients/PlatformApi';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import AuthService from 'models/AuthService';
import { AuthStore } from 'stores/AuthStore';
import * as CameraUtil from 'utils/CameraUtil';
import { type S3 } from 'utils/S3';

/**
 * Represents the details of a file (e.g., snapshot) to be shared with the Contextine API.
 * @property fileName - The name of the file, typically derived from the camera thumbnail.
 * @property blob - A base64-encoded string representing the file's content.
 * @property type - The MIME type of the file (e.g., 'jpg' for images).
 */
interface ContextineIFileDetails {
  fileName: string;
  blob: string;
  type: string;
}

export interface ContextineCredentials {
  token: string;
  refreshToken: string;
}

export function getToken(code: string, redirectUrl: string): Promise<ContextineCredentials> {
  const contextineApi = new ContextineAPI();
  const params = {
    code,
    redirect_uri: redirectUrl,
    name: 'Contextine',
    client_id: contextineApi.clientId,
  };

  return OAuthApi()
    .getToken(params, AuthService.authToken)
    .then((res) => {
      return {
        token: res.access_token,
        refreshToken: res.refresh_token,
      };
    });
}

export async function shareEvent(
  playingCameras: PlayingCamera[],
  url: string,
  event: IEvent,
  contextineCreds: ContextineCredentials | undefined,
  tenantId: string | undefined,
  s3: S3,
): Promise<
  { success: false; redirected: true } | { success: boolean; redirected?: false; data: string }
> {
  if (!s3) {
    throw new Error(`No s3`);
  }

  const contextineApi = new ContextineAPI();
  if (!contextineCreds || !contextineCreds.token) {
    const contextineAuthUrl = contextineApi._authUrl(url, event.id);

    const link = document.createElement('a');
    link.href = contextineAuthUrl;
    document.body.appendChild(link);
    link.click();

    return { success: false, redirected: true };
  }
  const customer = AuthStore.getCustomer();
  const snapshots = CameraUtil.getSnapshots(
    customer?.name,
    playingCameras,
    new Date(event.startTime).getTime(),
  );
  const snapshotsMap = snapshots.reduce(
    (acc, snapshot) => {
      acc[snapshot.playingCamera.chCameraId] = snapshot.imageURL;
      return acc;
    },
    {} as Record<string, string>,
  );

  const presignedUrls: { url: string; fileName: string; fileType: string }[] = [];
  const snapshotBlobs: ContextineIFileDetails[] = [];
  for (const c of event.cameras) {
    const thumbnail = await s3.getSignedUrlExistingObject(
      settings.s3_bucket_name,
      `${tenantId}/${c.thumbnail}`,
    );

    const fileName = c.thumbnail.replace('/', '_');
    const fileType = 'jpg';

    presignedUrls.push({
      url: thumbnail,
      fileName,
      fileType: 'jpg',
    });

    const blob = c.chId ? snapshotsMap[c.chId] : undefined;
    if (blob) {
      snapshotBlobs.push({
        fileName,
        type: fileType,
        blob,
      });
    }
  }

  const res = await contextineApi.createCase(
    contextineCreds.token,
    event,
    presignedUrls,
    snapshotBlobs,
    url,
  );
  return res.json();
}
